import axios from "axios";
import meta from "@/meta";
import TokenManager from "@/api/tokenManager/TokenManager";
import { sleep } from "@/helpers/supportHelpers";
import store from "@/store/index.js";

const riaApiClient = axios.create({
  baseURL: meta.apiBaseUrl
});

const skitudeApiClient = axios.create({
  baseURL: meta.skitudeApi.baseUrl,
  headers: {
    'x-api-key': meta.skitudeApi.xApiKey
  }
});

let isAlreadyFetchingAccessToken = false;
const tokenManager = new TokenManager();

const apiClient = {
  init() {
    const me = this
    riaApiClient.interceptors.response.use(
      function(response) {
        console.debug("API CLIENT: Response 2XX", response.config.url);
        return response;
      },
      async function(error) {
        const requestConfig = error.config;

        if (error.response && error.response.status === 401) {
          console.debug('API CLIENT: 401 recieved. Starting token renewal protocol');

          if (!isAlreadyFetchingAccessToken) {
            isAlreadyFetchingAccessToken = true;
            
            return me.requestForTokenRefresh().then((token) => {
              console.debug('APIClient: Repeating 401 call')
              requestConfig.headers["user-token"] = token;
              return riaApiClient.request(requestConfig)
            })
            .catch((error) => {
              console.error(error)
              return Promise.reject(error);
            });
          }

          console.debug('APIClient: Another request is renewing the token. Waiting for new token')

          try {
            await me.waitUntilTokenIsRefreshed();

            requestConfig.headers["user-token"] = riaApiClient.defaults.headers["user-token"];
            return riaApiClient.request(requestConfig)
          } catch(error) {
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );
  },
  requestForTokenRefresh() {
    const me = this
    return tokenManager.refreshToken()
      .then((token) => {
        console.debug(`APIClient: Recieved new token ${token}`)
        
        me.setApiToken(token)
        isAlreadyFetchingAccessToken = false;

        return token;
      }).catch((error) => {
        console.error('APIClient: Refresh Token Request error:', error)
        return Promise.reject(error)
      });
  },
  setApiToken(token) {
    meta.apiToken = token;
    riaApiClient.defaults.headers["user-token"] = token;
    store.dispatch('auth/setToken', token);
  },
  async waitUntilTokenIsRefreshed(elapsedTime = 0) {
    const me = this
    return await sleep(100).then(async () => {
      if (!isAlreadyFetchingAccessToken) {
        return
      }

      if (elapsedTime > 5000) {
        console.error('API Client: Waiting request timeout for token renew')
        return Promise.reject('timeout for token renew')
      }

      console.debug('APIClient: Token isn\'t newed yet')
      return await me.waitUntilTokenIsRefreshed(elapsedTime + 100)
    });
  },
  login(email, pwd) {
    return riaApiClient.post("/api/auth/login", {
      email,
      password: pwd
    });
  },
  registerUser(name, email, pwd) {
    return riaApiClient.post("/api/auth/register", {
      displayName: name,
      email,
      password: pwd
    });
  },
  validateKeyCard(resort_id,keyCard) {
    return riaApiClient.post(`/api/v1/resorts/${resort_id}/validate_keycard`,
    {"number": keyCard});
  },
  deleteKeyCard(keyCardId) {
    if(localStorage.getItem("accessToken")){
      return riaApiClient.delete(`/api/v1/keycards/${keyCardId}`);
    }
  },
  skitudeLogin(username, password) {
    return skitudeApiClient.post(
      '/custom-auth/v3/skitude/login',
      {
        email: username,
        password: password,
      },
      {
        headers: {
          'application-token': meta.skitudeApi.xApiKey
        }
      }
    )
  },
  restorePassword(email) {
    return skitudeApiClient.post(
      '/account/v3/password-forget',
      {
        email: email,
      },
      {
        headers: {
          'application-token': meta.skitudeApi.xApiKey
        }
      }
    )
  },
  skitudeRegisterCheckUsername(username)
  {
    return skitudeApiClient.get(
      `/account/v3/register/check?username=${username}`
    );
  },
  skitudeRegisterCheckEmail(email)
  {
    return skitudeApiClient.get(
      `/account/v3/register/check?email=${email}`
    );
  },
  skitudeRegister(registerForm)
  {
    if (!registerForm.isValidForm()) {
      return false;
    }

    const body = {
      "birthday": registerForm.birthdate(),
      "timezone": registerForm.timezone(),
      "acceptedSkitudeConditions": registerForm.acceptedTOS(),
      "language": store.getters['device/language'],
      "locale": store.getters['device/locale'],
      "acceptInfo": registerForm.acceptedCommunications(),
      "registerPlatform": "ria_" + window.location.hostname,
      "password": registerForm.password(),
      "surname": registerForm.surname(),
      "name": registerForm.name(),
      "email": registerForm.email(),
      "loginSource": "string",
      "username": registerForm.username(),
    }
    
    return skitudeApiClient.post(
      '/account/v3/register',
      body
    )
  }
};

export { apiClient };
export default riaApiClient;
